/**
 * Hook useReorderKanbanPhases - Reordena fases do Kanban de Conversas via drag & drop
 * Versão: 2.0.0
 * Correção: envia array JSON direto (sem JSON.stringify) para evitar erro 22023
 * Optimistic update alinhado com query keys que incluem filtros
 * Segue padrão do useReorderPipelinePhases (CRM)
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para depuração
const logger = createLogger('ReorderKanbanPhases');

// Interface para ordem das fases
interface PhaseOrder {
  phase_id: string;
  sort_order: number;
}

// Tipo para snapshot de rollback de múltiplas queries
interface RollbackSnapshot {
  entries: Array<{ queryKey: readonly unknown[]; data: unknown }>;
}

/**
 * Hook para reordenar fases do Kanban de Conversas
 * Usa função RPC segura com validação de tenant
 * 
 * @param categoryId - ID da categoria do Kanban
 * @param onSuccess - Callback opcional chamado após sucesso (ex: onRefresh)
 */
export function useReorderKanbanPhases(
  categoryId: string | null,
  onSuccessCallback?: () => void
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (phaseOrders: PhaseOrder[]) => {
      if (!categoryId) {
        throw new Error('categoryId é obrigatório para reordenar fases');
      }

      logger.debug('Reordenando fases do Kanban:', phaseOrders);

      // Converte para JSON compatível com Supabase - SEM JSON.stringify
      // O supabase-js serializa automaticamente para JSON
      const jsonOrders = phaseOrders.map(p => ({
        phase_id: p.phase_id,
        sort_order: p.sort_order
      }));

      // Chamada RPC enviando array JSON diretamente (igual padrão CRM)
      const { error } = await (supabase.rpc as any)('reorder_kanban_phases', {
        p_phase_orders: jsonOrders,
        p_category_id: categoryId,
        p_tenant_hint: currentTenant?.id || null
      });

      if (error) {
        logger.error('Erro ao reordenar fases do Kanban:', error);
        throw error;
      }

      logger.info('Fases do Kanban reordenadas com sucesso');
    },
    onMutate: async (phaseOrders) => {
      // Cancela TODAS as queries de stats dessa categoria (com qualquer filtro)
      await queryClient.cancelQueries({ 
        queryKey: ['kanban-conversation-phase-stats', categoryId],
        exact: false
      });

      // Snapshot de TODAS as queries de stats (com e sem filtros) para rollback
      const snapshot: RollbackSnapshot = { entries: [] };
      const queryCache = queryClient.getQueryCache();
      const matchingQueries = queryCache.findAll({
        queryKey: ['kanban-conversation-phase-stats', categoryId],
        exact: false
      });

      matchingQueries.forEach(query => {
        snapshot.entries.push({
          queryKey: query.queryKey,
          data: query.state.data
        });
      });

      // Cria mapa de nova ordem
      const orderMap = new Map(
        phaseOrders.map(p => [p.phase_id, p.sort_order])
      );

      // Optimistic update em TODAS as queries de stats dessa categoria
      matchingQueries.forEach(query => {
        queryClient.setQueryData(query.queryKey, (old: any) => {
          if (!old || !Array.isArray(old)) return old;
          
          // Aplica nova ordem e atualiza phase_position
          const updated = [...old]
            .map((item: any) => ({
              ...item,
              phase_position: orderMap.get(item.phase_id) ?? item.phase_position ?? 0
            }))
            .sort((a: any, b: any) => {
              const orderA = orderMap.get(a.phase_id) ?? a.phase_position ?? 0;
              const orderB = orderMap.get(b.phase_id) ?? b.phase_position ?? 0;
              return orderA - orderB;
            });
          
          return updated;
        });
      });

      return { snapshot };
    },
    onError: (err, _, context) => {
      // Rollback de TODAS as queries usando snapshot
      if (context?.snapshot) {
        context.snapshot.entries.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error('Erro ao reordenar fases');
    },
    onSuccess: () => {
      // Invalida TODAS as queries de stats da categoria para sincronizar
      queryClient.invalidateQueries({ 
        queryKey: ['kanban-conversation-phase-stats', categoryId],
        exact: false
      });
      
      // Dispara callback de refresh para sincronizar phases no useKanban
      onSuccessCallback?.();
      
      toast.success('Fases reordenadas');
    }
  });
}